import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Video, Phone, MapPin, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';
import { AppointmentEditModal } from './AppointmentEditModal';
import { SchedulingAssistant } from './SchedulingAssistant';

interface Participant {
  id: string;
  type: 'staff' | 'client';
  name: string;
  status?: 'pending' | 'confirmed' | 'declined' | 'tentative';
}

interface Appointment {
  id: string;
  organizer: Participant;
  attendees: Participant[];
  title: string;
  notes?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  created_at: string;
  updated_at: string;
  series_id?: string | null;
  series_rule?: {
    frequency: 'weekly' | 'biweekly' | 'monthly';
    count?: number;
    until?: string;
  } | null;
  instance_index?: number | null;
  is_exception?: boolean;
}

interface UpcomingSessionsBlockProps {
  clientId: string;
  clientName: string;
  clientTimezone?: string;
  coachTimezone?: string;
  onAppointmentUpdated?: () => void;
}

/**
 * UPCOMING SESSIONS BLOCK
 *
 * Displays the next 2-3 scheduled sessions for a client.
 * - 2-week lookahead window
 * - Max 3 sessions displayed
 * - Outlook-style summary cards
 * - Click opens appointment details modal
 */
export const UpcomingSessionsBlock: React.FC<UpcomingSessionsBlockProps> = ({
  clientId,
  clientName,
  clientTimezone = 'America/New_York',
  coachTimezone = 'America/New_York',
  onAppointmentUpdated
}) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // Collapse state - persist to localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('upcomingSessionsCollapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('upcomingSessionsCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  // Timeline edit mode state (for SchedulingAssistant)
  const [showSchedulingAssistant, setShowSchedulingAssistant] = useState(false);
  const [timelineEditAppointment, setTimelineEditAppointment] = useState<Appointment | null>(null);
  const [timelineEditScope, setTimelineEditScope] = useState<'single' | 'series' | undefined>(undefined);

  const fetchUpcomingSessions = async () => {
    try {
      const token = localStorage.getItem('auth_token');

      // Calculate date range: today to 2 weeks from now
      const today = new Date();
      const twoWeeksLater = new Date(today);
      twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);

      const startDate = today.toISOString().split('T')[0];
      const endDate = twoWeeksLater.toISOString().split('T')[0];

      const response = await fetch(
        `/api/appointments?attendee_id=${clientId}&start_date=${startDate}&end_date=${endDate}&status=scheduled`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();

      if (data.success) {
        // Sort by start time and take first 3
        const sorted = (data.appointments || [])
          .sort((a: Appointment, b: Appointment) =>
            new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
          )
          .slice(0, 3);

        setAppointments(sorted);
      }
    } catch (err: any) {
      console.error('[UpcomingSessions] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchUpcomingSessions();
    }
  }, [clientId]);

  // Format time for display: "2:00 PM"
  const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get relative day label: always short format for consistency
  const getRelativeDay = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Reset times for comparison
    const dateCompare = new Date(date);
    dateCompare.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);

    if (dateCompare.getTime() === today.getTime()) return 'Today';
    if (dateCompare.getTime() === tomorrow.getTime()) return 'Tmrw';

    // Always use short day name for consistency (Mon, Tue, Wed, etc.)
    return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  };

  // Get appointment type icon
  const getTypeIcon = (title: string) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('video') || titleLower.includes('zoom') || titleLower.includes('virtual')) {
      return <Video size={14} className="session-type-icon video" />;
    }
    if (titleLower.includes('phone') || titleLower.includes('call')) {
      return <Phone size={14} className="session-type-icon phone" />;
    }
    if (titleLower.includes('in-person') || titleLower.includes('office')) {
      return <MapPin size={14} className="session-type-icon in-person" />;
    }
    return <Calendar size={14} className="session-type-icon" />;
  };

  const handleAppointmentClick = (apt: Appointment) => {
    setSelectedAppointment(apt);
  };

  const handleModalClose = () => {
    setSelectedAppointment(null);
  };

  const handleAppointmentUpdated = () => {
    setSelectedAppointment(null);
    fetchUpcomingSessions();
    onAppointmentUpdated?.();
  };

  const handleAppointmentDeleted = () => {
    setSelectedAppointment(null);
    fetchUpcomingSessions();
    onAppointmentUpdated?.();
  };

  // Handle "Edit in Timeline" from modal - transition to SchedulingAssistant
  const handleEditInTimeline = (scope: 'single' | 'series' = 'single') => {
    if (!selectedAppointment) return;

    // Store the appointment and scope to edit in timeline
    setTimelineEditAppointment(selectedAppointment);
    setTimelineEditScope(scope);

    // Close the modal
    setSelectedAppointment(null);

    // Open the scheduling assistant (it will initialize in edit mode)
    setShowSchedulingAssistant(true);
  };

  // Handle appointment saved from SchedulingAssistant
  const handleSchedulingComplete = () => {
    setShowSchedulingAssistant(false);
    setTimelineEditAppointment(null);
    setTimelineEditScope(undefined);
    fetchUpcomingSessions();
    onAppointmentUpdated?.();
  };

  // Handle cancel from SchedulingAssistant
  const handleSchedulingCancel = () => {
    setShowSchedulingAssistant(false);
    setTimelineEditAppointment(null);
    setTimelineEditScope(undefined);
  };

  return (
    <>
      <div className={`card upcoming-sessions-block ${isCollapsed ? 'collapsed' : ''}`}>
        <style>{`
          .upcoming-sessions-block .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
          }

          .upcoming-sessions-block .section-header h3 {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            font-weight: 600;
            color: var(--text-primary);
            margin: 0;
          }

          .upcoming-sessions-block .section-header h3 svg {
            color: var(--accent-blue);
          }

          .upcoming-sessions-block .section-header h3 .count {
            color: var(--text-secondary);
            font-weight: 400;
          }

          .upcoming-sessions-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .session-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 10px 12px;
            background: var(--bg-primary);
            border: 1px solid var(--border-secondary);
            border-radius: var(--radius-medium);
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .session-card:hover {
            border-color: var(--accent-blue);
            background: var(--bg-secondary);
          }

          .session-date-badge {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 52px;
            flex-shrink: 0;
            padding: 6px 4px;
            background: var(--bg-secondary);
            border-radius: var(--radius-small);
            text-align: center;
          }

          .session-date-badge.today {
            background: var(--accent-blue);
            color: white;
          }

          .session-date-badge.today .date-relative,
          .session-date-badge.today .date-day {
            color: white;
          }

          .date-relative {
            font-size: 10px;
            font-weight: 600;
            color: var(--text-secondary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .date-day {
            font-size: 14px;
            font-weight: 700;
            color: var(--text-primary);
            line-height: 1.2;
          }

          .session-info {
            flex: 1;
            min-width: 0;
          }

          .session-title {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 4px;
          }

          .session-type-icon {
            flex-shrink: 0;
          }

          .session-type-icon.video { color: var(--accent-blue); }
          .session-type-icon.phone { color: var(--accent-green); }
          .session-type-icon.in-person { color: var(--accent-purple); }

          .session-meta {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .session-meta svg {
            opacity: 0.7;
          }

          .session-time {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .session-duration {
            color: var(--text-tertiary);
          }

          .session-arrow {
            color: var(--text-tertiary);
            opacity: 0;
            transition: opacity 0.15s ease;
          }

          .session-card:hover .session-arrow {
            opacity: 1;
          }

          .sessions-empty {
            padding: 16px;
            text-align: center;
            color: var(--text-secondary);
            font-size: 13px;
          }

          .sessions-loading {
            padding: 16px;
            text-align: center;
            color: var(--text-tertiary);
            font-size: 12px;
          }

          .upcoming-sessions-block .section-header {
            cursor: pointer;
          }

          .upcoming-sessions-block .section-header:hover {
            background: var(--bg-tertiary);
            margin: -20px -20px 12px -20px;
            padding: 20px 20px 8px 20px;
            border-radius: var(--radius-large) var(--radius-large) 0 0;
          }

          .upcoming-sessions-block.collapsed .section-header {
            margin-bottom: 0;
            border-bottom: none;
          }

          .upcoming-sessions-block.collapsed .section-header:hover {
            margin: -20px;
            padding: 20px;
            border-radius: var(--radius-large);
          }

          .collapse-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            background: none;
            border: none;
            color: var(--text-secondary);
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s;
          }

          .collapse-btn:hover {
            background: var(--bg-tertiary);
            color: var(--text-primary);
          }

          /* Scheduling Assistant Overlay */
          .scheduling-assistant-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 24px;
          }

          .scheduling-assistant-container {
            position: relative;
            width: 100%;
            max-width: 900px;
            max-height: 90vh;
            background: var(--bg-secondary);
            border-radius: var(--radius-large);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
            overflow: hidden;
          }

          .scheduling-assistant-overlay .close-btn {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--bg-primary);
            border: 1px solid var(--border);
            border-radius: 50%;
            color: var(--text-secondary);
            cursor: pointer;
            z-index: 10;
            transition: all 0.15s;
          }

          .scheduling-assistant-overlay .close-btn:hover {
            background: var(--bg-secondary);
            color: var(--text-primary);
            border-color: var(--accent-green);
          }
        `}</style>

        <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <h3>
            <Calendar size={16} /> Upcoming Sessions
            {appointments.length > 0 && (
              <span className="count">({appointments.length})</span>
            )}
          </h3>
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>

        {!isCollapsed && (loading ? (
          <div className="sessions-loading">Loading sessions...</div>
        ) : error ? (
          <div className="sessions-empty">Unable to load sessions</div>
        ) : appointments.length === 0 ? (
          <div className="sessions-empty">No upcoming sessions in the next 2 weeks</div>
        ) : (
          <div className="upcoming-sessions-list">
            {appointments.map(apt => {
              const relativeDay = getRelativeDay(apt.start_time);
              const isToday = relativeDay === 'Today';
              const dateObj = new Date(apt.start_time);

              return (
                <div
                  key={apt.id}
                  className="session-card"
                  onClick={() => handleAppointmentClick(apt)}
                >
                  <div className={`session-date-badge ${isToday ? 'today' : ''}`}>
                    <span className="date-relative">{relativeDay}</span>
                    <span className="date-day">{dateObj.getDate()}</span>
                  </div>

                  <div className="session-info">
                    <div className="session-title">
                      {getTypeIcon(apt.title)}
                      <span>{apt.title}</span>
                    </div>
                    <div className="session-meta">
                      <span className="session-time">
                        <Clock size={12} />
                        {formatTime(apt.start_time)}
                      </span>
                      <span className="session-duration">
                        ({apt.duration_minutes} min)
                      </span>
                    </div>
                  </div>

                  <ChevronRight size={16} className="session-arrow" />
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Appointment Details Modal */}
      {selectedAppointment && (
        <AppointmentEditModal
          appointment={selectedAppointment}
          clientName={clientName}
          clientTimezone={clientTimezone}
          coachTimezone={coachTimezone}
          isOpen={true}
          onClose={handleModalClose}
          onUpdated={handleAppointmentUpdated}
          onDeleted={handleAppointmentDeleted}
          onEditInTimeline={handleEditInTimeline}
        />
      )}

      {/* Scheduling Assistant Modal (for timeline editing) */}
      {showSchedulingAssistant && (
        <div className="scheduling-assistant-overlay">
          <div className="scheduling-assistant-container">
            <button
              className="close-btn"
              onClick={handleSchedulingCancel}
            >
              <X size={20} />
            </button>
            <SchedulingAssistant
              clientId={clientId}
              clientName={clientName}
              clientTimezone={clientTimezone}
              initialDate={timelineEditAppointment?.start_time.split('T')[0]}
              initialEditAppointment={timelineEditAppointment || undefined}
              initialEditScope={timelineEditScope}
              onAppointmentCreated={handleSchedulingComplete}
              onCancel={handleSchedulingCancel}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default UpcomingSessionsBlock;
